import { Component, OnInit, OnDestroy, Input, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AlertService } from 'app/core/services/alert.service';
import { Alert, AlertType } from 'app/core/data/interfaces/alert';

@Component({
    selector: 'app-alert',
    standalone: true,
    imports: [CommonModule, MatIconModule],
    templateUrl: './alert.html',
    styleUrl: './alert.scss'
})
export class AlertComponent implements OnInit, OnDestroy {
    private _router = inject(Router);
    private _alertService = inject(AlertService);
    private _elementRef = inject(ElementRef);

    @Input() id = 'default-alert';
    @Input() fade = true;

    alerts: Alert[] = [];
    alertSubscription!: Subscription;
    routeSubscription!: Subscription;

    ngOnInit(): void {
        this.alertSubscription = this._alertService.onAlert(this.id)
            .subscribe(alert => {
                if (!alert.message) {
                    this.alerts = this.alerts.filter(x => x.keepAfterRouteChange);
                    this.alerts.forEach(x => delete x.keepAfterRouteChange);
                    return;
                }

                this.alerts.push(alert);

                this.scrollToAlert();

                if (alert.autoClose) {
                    setTimeout(() => this.removeAlert(alert), 5000);
                }
            });

        this.routeSubscription = this._router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this._alertService.clear(this.id);
            }
        });
    }

    ngOnDestroy(): void {
        this.alertSubscription.unsubscribe();
        this.routeSubscription.unsubscribe();
    }

    removeAlert(alert: Alert): void {
        if (!this.alerts.includes(alert)) return;

        if (this.fade) {
            alert.fade = true;
            setTimeout(() => {
                this.alerts = this.alerts.filter(x => x !== alert);
            }, 250);
        } else {
            this.alerts = this.alerts.filter(x => x !== alert);
        }
    }

    getAlertClass(alert: Alert): string {
        const classes = ['alert'];

        switch (alert.type) {
            case AlertType.Success:
                classes.push('alert-success');
                break;
            case AlertType.Error:
                classes.push('alert-error');
                break;
            case AlertType.Info:
                classes.push('alert-info');
                break;
            case AlertType.Warning:
                classes.push('alert-warning');
                break;
        }

        if (alert.fade) {
            classes.push('fade');
        }

        return classes.join(' ');
    }

    getAlertIcon(alert: Alert): string {
        switch (alert.type) {
            case AlertType.Success:
                return 'check_circle';
            case AlertType.Error:
                return 'error';
            case AlertType.Info:
                return 'info';
            case AlertType.Warning:
                return 'warning';
            default:
                return 'info';
        }
    }

    private scrollToAlert(): void {
        setTimeout(() => {
            this._elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }
}
